import React from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { SchemaMarkup } from './SchemaMarkup';

interface FAQ {
  id: string;
  question: string;
  answer: string;
  order: number;
}

interface FAQSectionProps {
  faqs: FAQ[];
  title?: string;
  description?: string;
}

export function FAQSection({ faqs, title = "Frequently Asked Questions", description }: FAQSectionProps) {
  const [openId, setOpenId] = React.useState<string | null>(null);

  // Sort FAQs by order
  const sortedFaqs = React.useMemo(() => {
    return [...(faqs || [])].sort((a, b) => a.order - b.order);
  }, [faqs]);

  if (!sortedFaqs?.length) return null;

  return (
    <div className="w-full max-w-3xl mx-auto">
      <SchemaMarkup type="FAQ" data={{ questions: sortedFaqs }} />
      <div className="text-center mb-8">
        <h2 className="text-3xl font-bold text-gray-900 mb-4">{title}</h2>
        {description && (
          <p className="text-lg text-gray-600">{description}</p>
        )}
      </div>

      <div className="space-y-4">
        {sortedFaqs.map((faq) => (
          <div
            key={faq.id}
            className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden"
          >
            <button
              onClick={() => setOpenId(openId === faq.id ? null : faq.id)}
              className="w-full px-6 py-4 text-left flex justify-between items-center hover:bg-gray-50"
            >
              <span className="text-lg font-medium text-gray-900">{faq.question}</span>
              {openId === faq.id ? (
                <ChevronUp className="w-5 h-5 text-gray-500" />
              ) : (
                <ChevronDown className="w-5 h-5 text-gray-500" />
              )}
            </button>
            {openId === faq.id && (
              <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
                <p className="text-gray-700 whitespace-pre-wrap">{faq.answer}</p>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}